<template>
    <div class="pl-4 pt-3 row nav-padding"
         v-loading="loading">
        <el-form class="w-full"
                 label-width="130px"
                 label-position="top"
                 ref="local_presence_number_replacement"
                 :model="replacement"
                 :rules="formRules">

            <div class="row mb-3">
                <div class="col-12">
                    <el-alert
                        title="How to use"
                        type="success"
                        :closable="false">
                        <template v-slot>
                            <ul>
                                <li>First select a company</li>
                                <li>Select an LP package</li>
                                <li>Select numbers that you need to replace with new numbers</li>
                                <li>Click on Replace button to replace the numbers</li>
                            </ul>
                        </template>
                    </el-alert>
                    <el-alert
                        type="warning"
                        :closable="false">
                        <span style="color: #606266">
                            Note: New numbers will be purchased first before removing the selected numbers.
                        </span>
                    </el-alert>
                </div>
            </div>

            <div class="row">
                <div class="col-6">
                    <el-form-item label="Company"
                                  prop="company_id">
                        <company-auto-suggest placeholder="Search for the company"
                                              v-model="replacement.company_id"
                                              @input="fetchLpLines">
                        </company-auto-suggest>
                    </el-form-item>
                </div>
                <div class="col-6">
                    <el-form-item label="LP Package"
                                  prop="campaign_id">
                        <el-select class="w-full"
                                   v-model="replacement.campaign_id"
                                   placeholder="Select an LP package"
                                   :disabled="lpLines.length === 0"
                                   filterable
                                   @change="fetchPhoneNumbers">
                            <el-option
                                v-for="lpLine in lpLines"
                                :key="lpLine.id"
                                :label="'#' + lpLine.id + ' ' + lpLine.name"
                                :value="lpLine.id">
                            </el-option>
                        </el-select>
                    </el-form-item>
                </div>
            </div>

            <div class="row">
                <div class="col-11">
                    <el-form-item label="Phone Numbers"
                                  prop="incoming_number_ids">
                        <el-select class="w-full"
                                   v-model="replacement.incoming_number_ids"
                                   placeholder="Select Phone Numbers"
                                   :disabled="incomingNumbers.length === 0"
                                   @change="handleIncomingNumberChange"
                                   filterable
                                   multiple
                                   clearable>
                            <el-option
                                v-for="incomingNumber in incomingNumbers"
                                :key="incomingNumber.id"
                                :label="incomingNumber.phone_number"
                                :value="incomingNumber.id">
                            </el-option>
                        </el-select>
                    </el-form-item>
                </div>
                <div class="col-1 pt-4">
                    <el-checkbox :disabled="incomingNumbers.length === 0" :indeterminate="isIndeterminate" v-model="selectAll" @change="handleSelectAllChange">Select all</el-checkbox>
                </div>
            </div>

            <div class="row justify-content-end">
                <div class="col-3">
                    <el-form-item>
                        <el-button type="success"
                                   size="small"
                                   class="w-full mt-3"
                                   :loading="loading"
                                   :disabled="!validateForm(form_name) || loading"
                                   @click="processReplacement">
                            Replace
                        </el-button>
                    </el-form-item>
                </div>
            </div>
        </el-form>
    </div>
</template>

<script>
import auth from '../../auth'
import CompanyAutoSuggest from '../company-autosuggest'
import {form_validation_mixin} from '../../mixins'

export default {
    components: {
        CompanyAutoSuggest,
    },

    mixins: [
        form_validation_mixin
    ],

    data() {
        return {
            loading: false,
            auth: auth,
            form_name: 'local_presence_number_replacement',
            selectAll: false,
            replacement: {
                company_id: null,
                campaign_id: null,
                incoming_number_ids: []
            },
            formRules: {
                company_id: {
                    required: true,
                    message: 'Select a company to start',
                    trigger: 'blur'
                },
                campaign_id: {
                    required: true,
                    message: 'Select an LP package',
                    trigger: 'blur'
                },
                incoming_number_ids: {
                    required: true,
                    message: 'Select numbers to replace',
                    trigger: 'blur'
                },
            },
            showResultDialog: false,
            restoreResult: [],
            lpLines: [],
            incomingNumbers: [],
            lpLineCancelToken: null,
            lpLinesSource: null,
            incomingNumberCancelToken: null,
            incomingNumberSource: null,
        }
    },

    mounted() {
        this.lpLineCancelToken = axios.CancelToken;
        this.lpLinesSource = this.lpLineCancelToken.source();
        this.incomingNumberCancelToken = axios.CancelToken;
        this.incomingNumberSource = this.incomingNumberCancelToken.source();
    },

    computed: {
        isIndeterminate() {
            return this.replacement.incoming_number_ids.length > 0 && this.replacement.incoming_number_ids.length < this.incomingNumbers.length
        }
    },

    methods: {
        handleIncomingNumberChange(value) {
            this.selectAll = value.length === this.incomingNumbers.length
        },

        handleSelectAllChange(value) {
            this.replacement.incoming_number_ids = value ? this.incomingNumbers.map(item => item.id) : []
        },

        fetchLpLines() {
            this.lpLinesSource.cancel('fetchLpLines canceled by the user.')
            this.lpLinesSource = this.lpLineCancelToken.source()
            this.lpLines = []
            this.replacement.campaign_id = null
            this.replacement.incoming_number_ids = []
            if (this.replacement.company_id === '') {
                return
            }

            axios.get(`/api/admin/company/${this.replacement.company_id}/lp-lines`, {
                cancelToken: this.lpLinesSource.token
            })
                .then(res => {
                    this.lpLines = res.data
                })
                .catch(err => {
                    if (axios.isCancel(err)) {
                        console.log(err.message)
                    } else {
                        this.$notify.error({
                            title: 'Error searching for LP Packages',
                            message: err.response.data.message,
                        });
                    }
                })
        },

        fetchPhoneNumbers() {
            if (this.replacement.company_id === '') {
                return
            }

            this.incomingNumberSource.cancel('fetchPhoneNumbers canceled by the user.')
            this.incomingNumberSource = this.incomingNumberCancelToken.source()
            this.replacement.incoming_number_ids = []
            axios.get(`/api/admin/company/${this.replacement.company_id}/campaigns/${this.replacement.campaign_id}/incoming-numbers`, {
                cancelToken: this.incomingNumberSource.token
            })
                .then(res => {
                    this.incomingNumbers = res.data
                })
                .catch(err => {
                    if (axios.isCancel(err)) {
                        console.log(err.message)
                    } else {
                        this.$notify.error({
                            title: 'Error searching for phone numbers',
                            message: err.response.data.message,
                        });
                    }
                })
        },

        processReplacement() {
            if (this.validateForm(this.form_name)) {
                this.loading = true

                let url = `/api/admin/campaigns/${this.replacement.campaign_id}/lp/replace`
                let params = {
                    incoming_number_ids: this.replacement.incoming_number_ids
                }

                axios.post(url, params)
                    .then(res => {
                        return this.$notify.success({
                            title: 'Replacement',
                            message: res.data.message,
                        });
                    })
                    .catch(err => {
                        return this.$notify.error({
                            title: 'Replacement failed',
                            message: err.response.data.message,
                        });
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        },

        processRefillLP() {
            this.loading = true

            axios.post(`/api/admin/campaigns/${this.replacement.campaign_id}/lp/refill`)
                .then(res => {
                    // TODO: Show the result
                    this.$notify.success({
                        title: 'Refill',
                        message: 'Refill completed successfully',
                    })
                })
                .catch(err => {
                    return this.$notify.error({
                        title: 'Refill failed',
                        message: err.response.data.message,
                    })
                })
                .finally(() => {
                    this.loading = false
                })
        },
    },
}
</script>
